/**
 * The site plan of the Permit workspace: the design's roof faces and panels in local ENU metres (x east, y north;
 * drawn with north up like the editor's 2D view, no imagery), with the meter (M), the main service panel (MSP) and
 * the conductor path the installer places, the equipment locations (inverter, disconnects, combiner, battery…) and
 * the property lines; the open map data (parcel, roads) is drawn underneath for reference. Also the Structure step's
 * small roof map (read only, faces clickable).
 */
import { Fence, Gauge, Minus, MousePointer2, Plus, Route, Square, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as RPointerEvent } from "react";
import type { S } from "../api/client";
import { cx } from "../components/ui";
import { useT } from "../i18n/I18nProvider";
import type { MessageKey } from "../i18n/keys";
import { labelSpots } from "../lib/labelSpots";

type P = [number, number];
type Point = { x: number; y: number };

export interface PlanShapes {
  faces: { id: string; label: string; pts: P[]; center: P; holes: P[][] }[];
  panels: { face: string; pts: P[] }[];
  outline: P[] | null;
  bbox: { minX: number; minY: number; maxX: number; maxY: number };
}

/** The roof faces (from the roof model's vertices) and the design's panels, in plan. */
export function planShapes(doc: S["EditorDocument"]): PlanShapes {
  const roof = doc.roof_model;
  const vx = new Map((roof?.vertices ?? []).map((v) => [v.id, [v.xyz[0], v.xyz[1]] as P]));
  const faces = (roof?.faces ?? []).map((f) => {
    const pts = f.boundary.map((id) => vx.get(id)).filter((p): p is P => !!p);
    const center: P = pts.length ? [pts.reduce((a, p) => a + p[0], 0) / pts.length, pts.reduce((a, p) => a + p[1], 0) / pts.length] : [0, 0];
    const holes = (f.holes ?? []).map((h) => h.map((id) => vx.get(id)).filter((p): p is P => !!p)).filter((h) => h.length >= 3);
    return { id: f.id, label: f.label, pts, center, holes };
  });
  const panels = doc.panels.map((p) => ({ face: p.face_id, pts: p.corners_xyz.map((c) => [c[0], c[1]] as P) }));
  const outline = doc.roof_outline ? doc.roof_outline.map((p) => [p[0], p[1]] as P) : null;
  const all = [...faces.flatMap((f) => f.pts), ...(outline ?? [])];
  const xs = all.map((p) => p[0]);
  const ys = all.map((p) => p[1]);
  const bbox = all.length ? { minX: Math.min(...xs), minY: Math.min(...ys), maxX: Math.max(...xs), maxY: Math.max(...ys) } : { minX: -10, minY: -10, maxX: 10, maxY: 10 };
  return { faces, panels, outline, bbox };
}

export type SiteTool = "select" | "meter" | "main_panel" | "path" | "equipment" | "property";
export type EquipmentKind = S["PermitEquipmentLocationKind"];

export interface SiteMarks {
  meter?: Point | null;
  main_panel?: Point | null;
  conductor_path?: Point[] | null;
  equipment_locations?: S["PermitEquipmentLocation"][] | null;
  property_lines?: Point[][] | null;
}

/** Open map data drawn under the plan (read only). */
export interface SiteContext {
  parcel?: Point[][];
  roads?: { name?: string | null; points: Point[] }[];
}

/** `label` is a catalog key (render it with t()); `abbr` is the plan set's code, never translated. */
export const EQUIPMENT_KINDS: { value: EquipmentKind; abbr: string; label: MessageKey }[] = [
  { value: "inverter", abbr: "INV", label: "permit.sitePlan.equipment.inverter" },
  { value: "ac_disconnect", abbr: "ACD", label: "permit.sitePlan.equipment.acDisconnect" },
  { value: "dc_disconnect", abbr: "DCD", label: "permit.sitePlan.equipment.dcDisconnect" },
  { value: "combiner", abbr: "CMB", label: "permit.sitePlan.equipment.combiner" },
  { value: "battery", abbr: "BAT", label: "permit.sitePlan.equipment.battery" },
  { value: "sub_panel", abbr: "SP", label: "permit.sitePlan.equipment.subPanel" },
  { value: "production_meter", abbr: "PM", label: "permit.sitePlan.equipment.productionMeter" },
  { value: "ev_charger", abbr: "EV", label: "permit.sitePlan.equipment.evCharger" },
  { value: "other", abbr: "EQ", label: "permit.sitePlan.equipment.other" },
];
const abbrOf = (k: EquipmentKind) => EQUIPMENT_KINDS.find((e) => e.value === k)?.abbr ?? "EQ";

const r1 = (v: number) => Math.round(v * 100) / 100;

export function SitePlan({
  shapes,
  marks,
  onMarks,
  editable = false,
  tool = "select",
  onTool,
  highlightFace,
  onFace,
  faceStatus,
  compact = false,
  equipmentKind = "inverter",
  context,
}: {
  shapes: PlanShapes;
  marks?: SiteMarks;
  onMarks?: (m: SiteMarks) => void;
  editable?: boolean;
  tool?: SiteTool;
  onTool?: (t: SiteTool) => void;
  highlightFace?: string | null;
  onFace?: (id: string) => void;
  faceStatus?: Record<string, string>;
  compact?: boolean;
  /** What the "equipment" tool places. */
  equipmentKind?: EquipmentKind;
  context?: SiteContext;
}) {
  const t = useT();
  const svgRef = useRef<SVGSVGElement>(null);
  const b = shapes.bbox;
  const pad = compact ? 2 : 4;
  // framed on the roof and the markers placed when the plan opened (not re-framed while dragging)
  // re-framed when the set of things changes (lines or equipment added, the parcel loaded), not while dragging
  const first = useRef(marks);
  const extent = `${(marks?.property_lines ?? []).length}/${(marks?.equipment_locations ?? []).length}/${context?.parcel?.length ?? 0}`;
  const base = useMemo(() => {
    first.current = marks;
    const m = first.current;
    const placed = [m?.meter, m?.main_panel, ...(m?.conductor_path ?? []), ...(m?.equipment_locations ?? []), ...(m?.property_lines ?? []).flat(), ...(context?.parcel ?? []).flat()].filter((q): q is Point => !!q);
    const xs = [b.minX, b.maxX, ...placed.map((q) => q.x)];
    const ys = [b.minY, b.maxY, ...placed.map((q) => q.y)];
    const [x0, x1, y0, y1] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
    const wide = compact ? 1 : 4 / 3;
    return { cx: (x0 + x1) / 2, cy: (y0 + y1) / 2, span: Math.max((x1 - x0) / wide, y1 - y0) + 2 * pad };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [b.minX, b.maxX, b.minY, b.maxY, pad, compact, extent]);
  const [view, setView] = useState(base);
  useEffect(() => setView(base), [base]);
  const [hover, setHover] = useState<Point | null>(null);
  const drag = useRef<{ kind: "meter" | "main_panel" | "vertex" | "equipment" | "pvertex" | "pan"; index?: number; line?: number; start: Point; view: typeof view } | null>(null);
  const k = view.span / 30; // marker size in metres
  // face labels where each fits clear of the others (a dormer's faces and its host face; lib/labelSpots.ts)
  const labelSize = compact ? view.span / 9 : k * 1.3;
  const spots = useMemo(() => labelSpots(shapes.faces, labelSize), [shapes.faces, labelSize]);
  const path = marks?.conductor_path ?? [];
  const equipment = marks?.equipment_locations ?? [];
  const lines = marks?.property_lines ?? [];
  // the property line being drawn: committed to the marks once it has two points
  const drawing = useRef<{ index: number; points: Point[] } | null>(null);
  const [drawn, setDrawn] = useState<Point[]>([]);
  useEffect(() => {
    if (tool !== "property") {
      drawing.current = null;
      setDrawn([]);
    } else if (!drawing.current) drawing.current = { index: lines.length, points: [] };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tool]);

  const toPlan = useCallback((clientX: number, clientY: number): Point => {
    const svg = svgRef.current!;
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    const p = pt.matrixTransform(svg.getScreenCTM()!.inverse());
    return { x: r1(p.x), y: r1(-p.y) };
  }, []);

  const finishPath = useCallback(() => onTool?.("select"), [onTool]);
  useEffect(() => {
    if (tool !== "path" && tool !== "property") return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Enter" || e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        finishPath();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [tool, finishPath]);

  const down = (e: RPointerEvent<SVGSVGElement>) => {
    const p = toPlan(e.clientX, e.clientY);
    const target = (e.target as Element).closest("[data-mark]") as SVGElement | null;
    if (editable && tool === "meter") {
      onMarks?.({ ...marks, meter: p });
      onTool?.("select");
      return;
    }
    if (editable && tool === "main_panel") {
      onMarks?.({ ...marks, main_panel: p });
      onTool?.("select");
      return;
    }
    if (editable && tool === "path") {
      if (path.length < 60) onMarks?.({ ...marks, conductor_path: [...path, p] });
      return;
    }
    if (editable && tool === "equipment") {
      if (equipment.length < 24) onMarks?.({ ...marks, equipment_locations: [...equipment, { kind: equipmentKind, x: p.x, y: p.y }] });
      onTool?.("select");
      return;
    }
    if (editable && tool === "property" && drawing.current) {
      const cur = drawing.current;
      if (cur.points.length >= 200) return;
      cur.points = [...cur.points, p];
      setDrawn(cur.points);
      if (cur.points.length >= 2) onMarks?.({ ...marks, property_lines: [...lines.slice(0, cur.index), cur.points] });
      return;
    }
    if (editable && target) {
      const kind = target.dataset.mark as "meter" | "main_panel" | "vertex" | "equipment" | "pvertex";
      drag.current = { kind, index: target.dataset.index ? Number(target.dataset.index) : undefined, line: target.dataset.line ? Number(target.dataset.line) : undefined, start: p, view };
      svgRef.current?.setPointerCapture(e.pointerId);
      return;
    }
    const face = (e.target as Element).closest("[data-face]") as SVGElement | null;
    if (face && onFace) onFace(face.dataset.face!);
    if (!compact) {
      drag.current = { kind: "pan", start: { x: e.clientX, y: e.clientY }, view };
      svgRef.current?.setPointerCapture(e.pointerId);
    }
  };
  const move = (e: RPointerEvent<SVGSVGElement>) => {
    const d = drag.current;
    if (!d) {
      if (tool === "path" || tool === "property") setHover(toPlan(e.clientX, e.clientY));
      return;
    }
    if (d.kind === "pan") {
      const svg = svgRef.current!;
      const scale = d.view.span / Math.min(svg.clientWidth, svg.clientHeight);
      setView({ ...d.view, cx: d.view.cx - (e.clientX - d.start.x) * scale, cy: d.view.cy + (e.clientY - d.start.y) * scale });
      return;
    }
    const p = toPlan(e.clientX, e.clientY);
    if (d.kind === "vertex" && d.index != null) onMarks?.({ ...marks, conductor_path: path.map((q, i) => (i === d.index ? p : q)) });
    else if (d.kind === "equipment" && d.index != null) onMarks?.({ ...marks, equipment_locations: equipment.map((q, i) => (i === d.index ? { ...q, x: p.x, y: p.y } : q)) });
    else if (d.kind === "pvertex" && d.index != null && d.line != null)
      onMarks?.({ ...marks, property_lines: lines.map((l, li) => (li === d.line ? l.map((q, i) => (i === d.index ? p : q)) : l)) });
    else onMarks?.({ ...marks, [d.kind]: p });
  };
  const up = () => {
    drag.current = null;
  };
  const zoom = (f: number) => setView((v) => ({ ...v, span: Math.max(4, Math.min(400, v.span * f)) }));
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg || compact) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      zoom(e.deltaY > 0 ? 1.12 : 1 / 1.12);
    };
    svg.addEventListener("wheel", onWheel, { passive: false });
    return () => svg.removeEventListener("wheel", onWheel);
  }, [compact]);

  const pts = (ps: P[]) => ps.map((p) => `${p[0]},${-p[1]}`).join(" ");
  // the full plan is 4:3 (the compact map square): the span is the height
  const wide = compact ? 1 : 4 / 3;
  const vb = `${view.cx - (view.span * wide) / 2} ${-view.cy - view.span / 2} ${view.span * wide} ${view.span}`;
  return (
    <div className={cx("site-plan", compact && "compact", editable && `tool-${tool}`)}>
      {!compact && (
        <div className="site-plan-tools" role="toolbar" aria-label={t("permit.sitePlan.tools")}>
          {editable && (
            <>
              <ToolBtn on={tool === "select"} onClick={() => onTool?.("select")} icon={<MousePointer2 size={15} />} label={t("permit.sitePlan.tool.move")} />
              <ToolBtn on={tool === "meter"} onClick={() => onTool?.("meter")} icon={<Gauge size={15} />} label={marks?.meter ? t("permit.sitePlan.tool.meterPlaced") : t("permit.sitePlan.tool.placeMeter")} />
              <ToolBtn on={tool === "main_panel"} onClick={() => onTool?.("main_panel")} icon={<Square size={15} />} label={marks?.main_panel ? t("permit.sitePlan.tool.mainPanelPlaced") : t("permit.sitePlan.tool.placeMainPanel")} />
              <ToolBtn on={tool === "path"} onClick={() => onTool?.(tool === "path" ? "select" : "path")} icon={<Route size={15} />} label={tool === "path" ? t("permit.sitePlan.tool.finishPath") : path.length ? t("permit.sitePlan.tool.extendPath") : t("permit.sitePlan.tool.drawPath")} />
              {path.length > 0 && <ToolBtn on={false} onClick={() => onMarks?.({ ...marks, conductor_path: [] })} icon={<Trash2 size={15} />} label={t("permit.sitePlan.tool.clearPath")} />}
              <ToolBtn on={tool === "equipment"} onClick={() => onTool?.(tool === "equipment" ? "select" : "equipment")} icon={<Square size={15} />} label={t("permit.sitePlan.tool.placeEquipment", { kind: equipmentKind })} />
              <ToolBtn
                on={tool === "property"}
                onClick={() => onTool?.(tool === "property" || lines.length >= 12 ? "select" : "property")}
                icon={<Fence size={15} />}
                label={tool === "property" ? t("permit.sitePlan.tool.finishLine") : t("permit.sitePlan.tool.drawPropertyLine")}
              />
            </>
          )}
          <span className="site-plan-zoom">
            <button onClick={() => zoom(1 / 1.25)} aria-label={t("permit.sitePlan.zoomIn")}><Plus size={15} /></button>
            <button onClick={() => zoom(1.25)} aria-label={t("permit.sitePlan.zoomOut")}><Minus size={15} /></button>
            <button onClick={() => setView(base)} className="small">{t("permit.sitePlan.fit")}</button>
          </span>
        </div>
      )}
      <svg ref={svgRef} viewBox={vb} className="site-plan-svg" onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} onPointerLeave={() => setHover(null)} role="img" aria-label={t("permit.sitePlan.label")}>
        <defs>
          <pattern id="sp-grid" width={5} height={5} patternUnits="userSpaceOnUse">
            <path d="M 5 0 L 0 0 0 5" fill="none" stroke="currentColor" strokeWidth={0.04} opacity={0.35} />
          </pattern>
        </defs>
        <rect x={view.cx - view.span * 2} y={-view.cy - view.span} width={view.span * 4} height={view.span * 2} fill="url(#sp-grid)" className="sp-grid" />
        {(context?.roads ?? []).map((r, i) => (
          <g key={`road${i}`} className="sp-road">
            <polyline points={r.points.map((q) => `${q.x},${-q.y}`).join(" ")} strokeWidth={6} />
            {r.name && r.points.length >= 2 && (
              <text x={(r.points[0]!.x + r.points[1]!.x) / 2} y={-(r.points[0]!.y + r.points[1]!.y) / 2} fontSize={k * 0.9}>
                {r.name}
              </text>
            )}
          </g>
        ))}
        {(context?.parcel ?? []).map((ring, i) => (
          <polygon key={`parcel${i}`} points={ring.map((q) => `${q.x},${-q.y}`).join(" ")} className="sp-parcel" strokeWidth={k * 0.12} strokeDasharray={`${k * 0.8} ${k * 0.4}`} />
        ))}
        {lines.map((l, li) =>
          l.length >= 2 ? <polyline key={`pl${li}`} points={l.map((q) => `${q.x},${-q.y}`).join(" ")} className="sp-property" strokeWidth={k * 0.18} strokeDasharray={`${k * 1.2} ${k * 0.3} ${k * 0.2} ${k * 0.3}`} /> : null,
        )}
        {tool === "property" && drawn.length === 1 && <circle cx={drawn[0]!.x} cy={-drawn[0]!.y} r={k * 0.3} className="sp-vertex property" />}
        {tool === "property" && hover && drawn.length > 0 && (
          <line x1={drawn.at(-1)!.x} y1={-drawn.at(-1)!.y} x2={hover.x} y2={-hover.y} className="sp-property ghost" strokeWidth={k * 0.12} />
        )}
        {shapes.outline && <polygon points={pts(shapes.outline)} className="sp-outline" />}
        {shapes.faces.map((f) => (
          <polygon key={f.id} data-face={f.id} points={pts(f.pts)} className={cx("sp-face", highlightFace === f.id && "on", faceStatus?.[f.id] && `st-${faceStatus[f.id]}`, onFace && "clickable")} />
        ))}
        {shapes.panels.map((p, i) => (
          <polygon key={i} points={pts(p.pts)} className={cx("sp-panel", highlightFace && p.face !== highlightFace && "dim")} />
        ))}
        {spots.map((spot) => (
          <g key={spot.id}>
            {spot.leader && <line x1={spot.leader[0]} y1={-spot.leader[1]} x2={spot.at[0]} y2={-spot.at[1]} className="sp-face-leader" strokeWidth={labelSize * 0.06} />}
            <text x={spot.at[0]} y={-spot.at[1]} className="sp-face-label" fontSize={labelSize}>
              {shapes.faces.find((f) => f.id === spot.id)?.label}
            </text>
          </g>
        ))}
        {!compact && (
          <g className="sp-north" transform={`translate(${view.cx + (view.span * wide) / 2 - k * 2.2} ${-view.cy - view.span / 2 + k * 2.6})`}>
            <path d={`M0 ${-k * 1.4} L${k * 0.6} ${k * 0.6} L0 ${k * 0.2} L${-k * 0.6} ${k * 0.6} Z`} />
            <text y={k * 2} fontSize={k * 0.9}>{t("permit.sitePlan.north")}</text>
          </g>
        )}
        {path.length > 0 && <polyline points={path.map((p) => `${p.x},${-p.y}`).join(" ")} className="sp-path" strokeWidth={k * 0.22} strokeDasharray={`${k * 0.6} ${k * 0.4}`} />}
        {tool === "path" && hover && path.length > 0 && <line x1={path[path.length - 1]!.x} y1={-path[path.length - 1]!.y} x2={hover.x} y2={-hover.y} className="sp-path ghost" strokeWidth={k * 0.15} />}
        {editable &&
          path.map((p, i) => <circle key={i} data-mark="vertex" data-index={i} cx={p.x} cy={-p.y} r={k * 0.35} className="sp-vertex" />)}
        {editable && lines.map((l, li) => l.map((q, i) => <circle key={`pv${li}-${i}`} data-mark="pvertex" data-line={li} data-index={i} cx={q.x} cy={-q.y} r={k * 0.3} className="sp-vertex property" />))}
        {equipment.map((e, i) => (
          <g key={`eq${i}`} data-mark="equipment" data-index={i} className="sp-mark equipment" transform={`translate(${e.x} ${-e.y})`}>
            <rect x={-k * 1.3} y={-k * 0.8} width={k * 2.6} height={k * 1.6} rx={k * 0.2} />
            <text fontSize={k * 0.75}>{abbrOf(e.kind)}</text>
          </g>
        ))}
        {marks?.main_panel && (
          <g data-mark="main_panel" className="sp-mark msp" transform={`translate(${marks.main_panel.x} ${-marks.main_panel.y})`}>
            <rect x={-k * 1.3} y={-k * 0.8} width={k * 2.6} height={k * 1.6} rx={k * 0.2} />
            {/* eslint-disable-next-line solaros-i18n/no-hardcoded-text -- the plan set's code for the main service panel */}
            <text fontSize={k * 0.8}>MSP</text>
          </g>
        )}
        {marks?.meter && (
          <g data-mark="meter" className="sp-mark meter" transform={`translate(${marks.meter.x} ${-marks.meter.y})`}>
            <circle r={k * 0.9} />
            <text fontSize={k * 0.95}>M</text>
          </g>
        )}
      </svg>
      {!compact && (
        <div className="site-plan-legend small muted">
          {editable && tool === "path"
            ? t("permit.sitePlan.legend.path")
            : editable && tool === "property"
              ? t("permit.sitePlan.legend.property")
              : editable && tool === "equipment"
                ? t("permit.sitePlan.legend.equipment")
                : editable && (tool === "meter" || tool === "main_panel")
                  ? t("permit.sitePlan.legend.placeMark", { mark: tool })
                  : editable
                    ? t("permit.sitePlan.legend.edit")
                    : t("permit.sitePlan.legend.view")}
        </div>
      )}
    </div>
  );
}

function ToolBtn({ on, onClick, icon, label }: { on: boolean; onClick: () => void; icon: React.ReactNode; label: string }) {
  return (
    <button className={cx("sp-tool", on && "on")} onClick={onClick} aria-pressed={on}>
      {icon}
      <span>{label}</span>
    </button>
  );
}
